import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";

const OutletContext = createContext(null);

const DEFAULT_OUTLET = {
  id: "",
  slug: "",
  name: "Restoran",
  tagline: "",
  phone: "",
  address: "",
  hours: "",
  logo_file_id: "",
  logo_url: "",
  header_image_file_id: "",
  header_image_url: "",
  login_image_file_id: "",
  login_image_url: "",
  primary_color: "#D14D36",
  tax_percent: 0,
  service_percent: 0,
  currency: "IDR",
  payment_note: "",
  qris_image_file_id: "",
  transfer_info: "",
  menu_open: true,
  closed_message: "Menu sedang tutup. Silakan kembali lagi nanti.",
  popup_enabled: false,
  popup_title: "",
  popup_message: "",
  promotion_enabled: false,
  promotion_title: "",
  promotion_description: "",
  promotion_image_file_id: "",
  promotion_image_url: "",
};

function hexToHsl(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function logoSrcOf(outlet) {
  if (!outlet) return "";
  return outlet.logo_file_id ? fileUrl(outlet.logo_file_id) : (outlet.logo_url || "");
}

/**
 * OutletContext provides:
 * - outlets: list of all outlets accessible (public list for customer, own outlets for admin)
 * - activeOutlet: current outlet in view (per super_admin's selection or based on auth/url)
 * - setActiveOutletId(id): switch active outlet
 * - refresh(): re-fetch outlets
 */
export function OutletProvider({ children, loadPublicOnMount = true }) {
  const [outlets, setOutlets] = useState([]);
  const [activeOutlet, setActiveOutlet] = useState(DEFAULT_OUTLET);
  const [loaded, setLoaded] = useState(false);

  const loadPublic = useCallback(async () => {
    try {
      const { data } = await api.get("/outlets/public");
      setOutlets(data || []);
      // Restore or default active outlet
      const savedId = localStorage.getItem("resto_active_outlet");
      const chosen = (data || []).find((o) => o.id === savedId) || (data || [])[0];
      if (chosen) setActiveOutlet((cur) => ({ ...cur, ...chosen }));
    } catch {}
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loadPublicOnMount) return undefined;
    loadPublic();
    return undefined;
  }, [loadPublic, loadPublicOnMount]);

  // Fetch full outlet by slug (public info incl tax/service/etc)
  const loadOutletBySlug = useCallback(async (slug) => {
    if (!slug) return null;
    try {
      const { data } = await api.get(`/outlets/by-slug/${slug}`);
      setActiveOutlet((cur) => ({ ...cur, ...data }));
      return data;
    } catch {
      return null;
    }
  }, []);

  const setActiveOutletId = useCallback((id) => {
    const found = outlets.find((o) => o.id === id);
    if (found) {
      setActiveOutlet((cur) => ({ ...cur, ...found }));
      localStorage.setItem("resto_active_outlet", id);
    }
  }, [outlets]);

  // Apply primary color + title
  useEffect(() => {
    const hsl = hexToHsl(activeOutlet.primary_color);
    if (hsl) {
      document.documentElement.style.setProperty("--primary", hsl);
      document.documentElement.style.setProperty("--ring", hsl);
    }
    if (activeOutlet.name) document.title = activeOutlet.name;
  }, [activeOutlet.primary_color, activeOutlet.name]);

  return (
    <OutletContext.Provider value={{
      outlets,
      setOutlets,
      activeOutlet,
      setActiveOutlet,
      setActiveOutletId,
      loadOutletBySlug,
      refresh: loadPublic,
      loaded,
      logoSrc: logoSrcOf(activeOutlet),
    }}>
      {children}
    </OutletContext.Provider>
  );
}

export const useOutlet = () => useContext(OutletContext);
